/**
 * Billing API — plans catalog, subscription status, CryptoPay invoices, promos.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, type ApiError } from "./client";

// ============================================================
// Types
// ============================================================

export interface Plan {
  id: string;
  title: string;
  tagline: string;
  price_usd: number;
  duration_days: number;
  max_accounts: number;
  max_chats: number;
  perks: string[];
  badge: string | null;
}

export interface SubscriptionStatus {
  plan_id: string | null;
  plan_title: string | null;
  expires_at: string | null;
  days_remaining: number | null;
  is_active: boolean;
  is_trial: boolean;
  /** True iff user has never claimed the 3-day trial AND has no active plan. */
  trial_available: boolean;
  /** True iff the user is currently subscribed to the news channel. */
  news_verified: boolean;
  /** Public link / @username of the news channel (null when gate disabled). */
  news_channel_url: string | null;
  max_accounts: number;
  max_chats: number;
}

export interface TrialActivateResponse {
  subscription: SubscriptionStatus;
}

export interface NewsCheckResponse {
  verified: boolean;
  channel_url: string | null;
  /** True when no channel is configured server-side — gate is off. */
  gate_disabled: boolean;
}

export interface Invoice {
  id: number;
  plan_id: string;
  plan_title: string;
  provider: "crypto_pay" | "telegram_stars";
  amount_usd: number;
  invoice_id: string;
  pay_url: string | null;
  status: "pending" | "paid" | "expired" | "canceled";
  paid_at: string | null;
  created_at: string;
}

export interface PromoActivateResponse {
  plan_id: string;
  plan_title: string;
  expires_at: string;
}

// ============================================================
// Queries
// ============================================================

export function usePlans() {
  return useQuery<Plan[], ApiError>({
    queryKey: ["billing", "plans"],
    queryFn: () => api.get<Plan[]>("/payments/plans"),
    staleTime: 5 * 60_000,
  });
}

export function useSubscription() {
  return useQuery<SubscriptionStatus, ApiError>({
    queryKey: ["billing", "subscription"],
    queryFn: () => api.get<SubscriptionStatus>("/payments/subscription"),
    staleTime: 30_000,
  });
}

export function useInvoices() {
  return useQuery<Invoice[], ApiError>({
    queryKey: ["billing", "invoices"],
    queryFn: () => api.get<Invoice[]>("/payments/invoices"),
    staleTime: 15_000,
  });
}

export function useInvoice(invoiceId: number | null, opts?: { refetchInterval?: number | false }) {
  return useQuery<Invoice, ApiError>({
    queryKey: ["billing", "invoice", invoiceId],
    queryFn: () => api.get<Invoice>(`/payments/invoice/${invoiceId}`),
    enabled: invoiceId != null,
    refetchInterval: opts?.refetchInterval ?? 4_000,
  });
}

// ============================================================
// Mutations
// ============================================================

export type PaymentProvider = "crypto_pay" | "telegram_stars";

export function useCreateInvoice() {
  const qc = useQueryClient();
  return useMutation<Invoice, ApiError, { plan_id: string; discount_code?: string; provider?: PaymentProvider }>({
    mutationFn: (body) => api.post<Invoice>("/payments/invoice", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["billing", "invoices"] });
    },
  });
}

export function useActivatePromo() {
  const qc = useQueryClient();
  return useMutation<PromoActivateResponse, ApiError, { code: string }>({
    mutationFn: (body) => api.post<PromoActivateResponse>("/payments/promo", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["billing", "subscription"] });
      qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

export function useActivateTrial() {
  const qc = useQueryClient();
  return useMutation<TrialActivateResponse, ApiError, void>({
    mutationFn: () => api.post<TrialActivateResponse>("/payments/trial/activate", {}),
    onSuccess: (data) => {
      qc.setQueryData(["billing", "subscription"], data.subscription);
      qc.invalidateQueries({ queryKey: ["billing", "subscription"] });
      qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

export function useCheckNewsSubscription() {
  const qc = useQueryClient();
  return useMutation<NewsCheckResponse, ApiError, void>({
    mutationFn: () =>
      api.post<NewsCheckResponse>("/payments/trial/check-subscription", {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["billing", "subscription"] });
      qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
}
